/**
 * DocumentsReport — Ordens de Serviço, Contratos e Recibos.
 *
 * Os três módulos que o concorrente NÃO tem e sobre os quais o app não colhia
 * nada. A OS vem completa (status, período, margem); contratos e recibos vêm com
 * contagem e valor por período/estado — o mínimo viável desta entrega, dito de
 * forma explícita em vez de meio-entregue nos três.
 *
 * Portões: **plano** (`reports_advanced`, PRO). **Sem portão de papel**: a RLS
 * das três tabelas é `SELECT is_company_member(...)`, então um `member` recebe
 * as linhas normalmente (≠ `finance_entries`, owner/admin-only, que é por isso
 * que o Relatório de Pagamentos exige `canSeeFinance` — #38).
 *
 * ⚠️ `receipts` NÃO tem coluna `status` (#57): "Emitido/Cancelado" é derivado de
 * `cancelled_at` em `documentsReport.receiptState`.
 */
import { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button3D } from '@/components/ui/button-3d';
// Fonte única do plural com contagem — ver CLAUDE.md #87.
import { plural } from '@/lib/plural';
import {
  ClipboardList,
  Crown,
  FileSignature,
  Percent,
  ReceiptText,
  Wrench,
} from 'lucide-react';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { formatBRL, formatBRLShort } from '@/lib/financeLabels';
import {
  AwaitingData,
  BlurOverlay,
  ListTail,
  WarningCard,
} from '@/components/reports/ReportGates';
import { MONEY_AXIS_WIDTH, moneyAxisTick } from '@/components/reports/axisFormat';
import {
  listLimitFor,
  ReportViewToggle,
  type ReportViewMode,
} from '@/components/reports/ReportViewToggle';
import type {
  ContractsReport,
  ReceiptsReport,
  ServiceOrdersReport,
  StatusBucket,
} from '@/lib/documentsReport';

const SUMMARY_LIMIT = 6;

// ===== amostra para o usuário grátis (será borrada) =====

const SAMPLE_STATUS: StatusBucket[] = [
  { status: 'pending', label: 'Pendente', shortLabel: 'Pend.', color: '#f59e0b', count: 4, total: 2100 },
  { status: 'in_progress', label: 'Em andamento', shortLabel: 'Andam.', color: '#0ea5e9', count: 3, total: 4600 },
  { status: 'completed', label: 'Concluída', shortLabel: 'Concl.', color: '#10b981', count: 11, total: 15800 },
  { status: 'cancelled', label: 'Cancelada', shortLabel: 'Canc.', color: '#ef4444', count: 1, total: 400 },
];

interface DocumentsReportProps {
  orders: ServiceOrdersReport;
  contracts: ContractsReport;
  receipts: ReceiptsReport;
  truncated?: boolean;
  loading?: boolean;
  loadFailed?: boolean;
  isOnline?: boolean;
  viewMode: ReportViewMode;
  onViewModeChange: (mode: ReportViewMode) => void;
  /** `reports_advanced.allowed`. */
  allowed: boolean;
  onUnlock: () => void;
}

export function DocumentsReport({
  orders,
  contracts,
  receipts,
  truncated = false,
  loading = false,
  loadFailed = false,
  isOnline = true,
  viewMode,
  onViewModeChange,
  allowed,
  onUnlock,
}: DocumentsReportProps) {
  const limit = listLimitFor(viewMode, SUMMARY_LIMIT);

  const orderRows = useMemo(() => orders.rows.slice(0, limit), [orders.rows, limit]);
  const contractRows = useMemo(() => contracts.rows.slice(0, limit), [contracts.rows, limit]);
  const receiptRows = useMemo(() => receipts.rows.slice(0, limit), [receipts.rows, limit]);

  /**
   * Dado do gráfico SEM nenhum campo que colida com prop do recharts (#42): ele
   * espalha os campos da entrada nas props, e `fill`, `name`, `value`, `percent`
   * e `index` são props DELE — um `percent` nosso em 0–100 já produziu "5556%"
   * na pizza do Relatório de Pagamentos. Aqui só vão `shortLabel` (eixo) e
   * `total` (valor); a cor viaja como `barColor`, nome que o recharts ignora, e
   * é aplicada por `<Cell>`.
   */
  const orderChart = useMemo(
    () =>
      orders.byStatus.map((b) => ({
        shortLabel: b.shortLabel,
        total: b.total,
        barColor: b.color,
      })),
    [orders.byStatus],
  );

  const Gate = ({ children }: { children: React.ReactNode }) => (
    <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
      {children}
    </BlurOverlay>
  );

  return (
    <div className="space-y-6">
      <Card className="bg-muted/30">
        <CardContent className="p-3">
          <p className="text-[11px] leading-snug text-muted-foreground">
            Ordens de serviço e contratos pela{' '}
            <span className="font-medium">data de criação</span> (mesma base da aba Orçamentos);
            recibos pela <span className="font-medium">data de emissão</span>.
          </p>
        </CardContent>
      </Card>

      {truncated && allowed && (
        <WarningCard>
          O período tem documentos demais e a leitura foi limitada. Os números abaixo estão
          incompletos — escolha um período menor.
        </WarningCard>
      )}

      {loadFailed && allowed && (
        <WarningCard>
          {isOnline
            ? 'Não foi possível ler os documentos do período — os números abaixo estão zerados.'
            : 'Sem conexão: os documentos do período precisam de internet. Os números abaixo estão zerados.'}
        </WarningCard>
      )}

      {allowed && (
        <ReportViewToggle
          value={viewMode}
          onChange={onViewModeChange}
          hint={
            viewMode === 'detailed'
              ? 'listas completas'
              : `${orders.count} OS · ${plural(contracts.count, 'contrato', 'contratos')} · `
                + plural(receipts.count, 'recibo', 'recibos')
          }
        />
      )}

      {/* ===================== ORDENS DE SERVIÇO ===================== */}

      <div className="grid grid-cols-3 gap-3">
        <KpiCard
          icon={<Wrench className="h-3.5 w-3.5 text-blue-600" />}
          tint="bg-blue-100 dark:bg-blue-950/30"
          label="Ordens de serviço"
          value={allowed ? String(orders.count) : '19'}
          valueClass="text-blue-600"
          hint="no período"
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<ClipboardList className="h-3.5 w-3.5 text-emerald-600" />}
          tint="bg-emerald-100 dark:bg-emerald-950/30"
          label="Concluídas"
          value={allowed ? formatBRLShort(orders.completedTotal) : 'R$ 15,8K'}
          valueClass="text-emerald-600"
          hint={plural(allowed ? orders.completedCount : 11, 'ordem', 'ordens')}
          allowed={allowed}
          onUnlock={onUnlock}
        />
        <KpiCard
          icon={<Percent className="h-3.5 w-3.5 text-purple-600" />}
          tint="bg-purple-100 dark:bg-purple-950/30"
          label="Margem"
          value={
            allowed
              ? orders.marginPercent == null
                ? '—'
                : `${orders.marginPercent.toFixed(0)}%`
              : '38%'
          }
          valueClass="text-purple-600"
          hint={
            allowed
              ? orders.margin == null
                ? 'sem custo'
                : formatBRLShort(orders.margin)
              : 'R$ 6,0K'
          }
          allowed={allowed}
          onUnlock={onUnlock}
        />
      </div>

      {allowed && orders.missingCostLines > 0 && (
        <WarningCard>
          {orders.missingCostLines}{' '}
          {orders.missingCostLines === 1
            ? 'linha de ordem de serviço está sem custo informado e ficou fora da margem'
            : 'linhas de ordens de serviço estão sem custo informado e ficaram fora da margem'}
          . A margem considera só {formatBRL(orders.costedRevenue)} de receita.
        </WarningCard>
      )}

      {/* A divergência com a aba Lucro é DELIBERADA e precisa estar escrita:
          lá a OS nascida de orçamento é pulada para não dobrar o CMV; aqui ela
          conta, porque o recorte é só de ordens de serviço. */}
      {allowed && orders.fromQuoteCount > 0 && (
        <Card className="bg-muted/30">
          <CardContent className="p-3">
            <p className="text-[11px] leading-snug text-muted-foreground">
              {orders.fromQuoteCount}{' '}
              {orders.fromQuoteCount === 1
                ? 'ordem nasceu de um orçamento e conta aqui'
                : 'ordens nasceram de um orçamento e contam aqui'}
              . Na aba <span className="font-medium">Lucro</span> (em /financeiro) elas são puladas
              de propósito: o custo delas já está no orçamento de origem, e contar os dois dobraria
              o CMV. Por isso o custo desta aba pode ser maior que o de lá.
            </p>
          </CardContent>
        </Card>
      )}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <Wrench className="h-4 w-4" />
            Ordens de serviço por status
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed && orders.isEmpty ? (
              <AwaitingData
                hint={
                  loading
                    ? 'Carregando documentos…'
                    : 'Nenhuma ordem de serviço no período selecionado'
                }
              />
            ) : (
              <div className="space-y-4">
                <div className="h-52">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={
                        allowed
                          ? orderChart
                          : SAMPLE_STATUS.map((s) => ({
                              shortLabel: s.shortLabel,
                              total: s.total,
                              barColor: s.color,
                            }))
                      }
                      margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" className="stroke-muted" />
                      {/* interval={0}: sem isso o recharts esconde rótulos que
                          colidiriam e sobra barra anônima (#51). Os rótulos já
                          são curtos ("Andam.", "Concl."). */}
                      <XAxis
                        dataKey="shortLabel"
                        interval={0}
                        tick={{ fontSize: 10 }}
                        className="text-muted-foreground"
                      />
                      <YAxis
                        tick={{ fontSize: 12 }}
                        tickFormatter={moneyAxisTick}
                        width={MONEY_AXIS_WIDTH}
                        className="text-muted-foreground"
                      />
                      <Tooltip
                        formatter={(value: number) => [formatBRL(value), 'Valor']}
                        contentStyle={{
                          backgroundColor: 'hsl(var(--popover))',
                          border: '1px solid hsl(var(--border))',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar dataKey="total" radius={[4, 4, 0, 0]}>
                        {(allowed ? orderChart : SAMPLE_STATUS).map((entry: any, index) => (
                          <Cell key={`os-${index}`} fill={entry.barColor ?? entry.color ?? '#0ea5e9'} />
                        ))}
                      </Bar>
                    </BarChart>
                  </ResponsiveContainer>
                </div>

                <div className="space-y-1">
                  {(allowed ? orders.byStatus : SAMPLE_STATUS).map((b) => (
                    <div
                      key={b.status}
                      className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                    >
                      <div className="flex min-w-0 items-center gap-2">
                        <span
                          className="h-2.5 w-2.5 shrink-0 rounded-full"
                          style={{ backgroundColor: b.color }}
                        />
                        <p className="truncate text-sm">{b.label}</p>
                      </div>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold">{formatBRLShort(b.total)}</p>
                        <p className="text-[11px] text-muted-foreground">
                          {b.count} {b.count === 1 ? 'ordem' : 'ordens'}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {allowed && orders.rows.length > 0 && (
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">
              {viewMode === 'detailed' ? 'Todas as ordens' : 'Ordens recentes'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {orderRows.map((row) => (
              <div
                key={row.id}
                className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
              >
                <div className="min-w-0 flex-1">
                  <div className="flex min-w-0 items-center gap-1.5">
                    <p className="truncate text-sm font-medium">{row.title}</p>
                    {/* Selo CURTO ao lado de nome variável (#52). */}
                    {row.fromQuote && (
                      <Badge variant="outline" className="shrink-0 text-[10px]">
                        Orç.
                      </Badge>
                    )}
                  </div>
                  <p className="truncate text-[11px] text-muted-foreground">
                    {row.clientName} · {formatIsoDate(row.date)} · {row.statusLabel}
                  </p>
                </div>
                <div className="shrink-0 text-right">
                  <p className="text-sm font-semibold">{formatBRLShort(row.total)}</p>
                  <p className="text-[11px] text-muted-foreground">
                    {row.cost == null ? 'sem custo' : `custo ${formatBRLShort(row.cost)}`}
                  </p>
                </div>
              </div>
            ))}
            <ListTail total={orders.rows.length} shown={orderRows.length} noun="ordem" />
          </CardContent>
        </Card>
      )}

      {/* ===================== CONTRATOS ===================== */}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <FileSignature className="h-4 w-4" />
            Contratos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed ? (
              contracts.isEmpty ? (
                <AwaitingData hint="Nenhum contrato criado no período selecionado" />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 p-3">
                    <div className="min-w-0">
                      <p className="text-sm text-muted-foreground">Assinados</p>
                      <p className="text-[11px] text-muted-foreground/70">
                        {contracts.signedCount} de {contracts.count}{' '}
                        {contracts.count === 1 ? 'contrato' : 'contratos'} no período
                      </p>
                    </div>
                    <p className="shrink-0 text-xl font-bold text-emerald-600">
                      {formatBRLShort(contracts.signedTotal)}
                    </p>
                  </div>

                  <div className="space-y-1">
                    {contracts.byStatus.map((b) => (
                      <div
                        key={b.status}
                        className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                      >
                        <div className="flex min-w-0 items-center gap-2">
                          <span
                            className="h-2.5 w-2.5 shrink-0 rounded-full"
                            style={{ backgroundColor: b.color }}
                          />
                          <p className="truncate text-sm">{b.label}</p>
                        </div>
                        <div className="shrink-0 text-right">
                          <p className="text-sm font-semibold">{formatBRLShort(b.total)}</p>
                          <p className="text-[11px] text-muted-foreground">
                            {b.count} {b.count === 1 ? 'contrato' : 'contratos'}
                          </p>
                        </div>
                      </div>
                    ))}
                  </div>

                  {viewMode === 'detailed' && (
                    <div className="space-y-2 border-t pt-2">
                      {contractRows.map((row) => (
                        <div
                          key={row.id}
                          className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{row.title}</p>
                            <p className="truncate text-[11px] text-muted-foreground">
                              {row.clientName} · {formatIsoDate(row.date)} · {row.statusLabel}
                            </p>
                          </div>
                          <p className="shrink-0 text-sm font-semibold">
                            {formatBRLShort(row.total)}
                          </p>
                        </div>
                      ))}
                      <ListTail
                        total={contracts.rows.length}
                        shown={contractRows.length}
                        noun="contrato"
                      />
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="space-y-2">
                {[
                  { s: 'Assinado', v: 'R$ 9,2K' },
                  { s: 'Enviado', v: 'R$ 3,4K' },
                  { s: 'Rascunho', v: 'R$ 1,1K' },
                ].map((row) => (
                  <div key={row.s} className="flex items-center justify-between border-b py-2 last:border-0">
                    <span className="text-sm">{row.s}</span>
                    <span className="text-sm">{row.v}</span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {/* ===================== RECIBOS ===================== */}

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="flex items-center gap-2 text-base">
            <ReceiptText className="h-4 w-4" />
            Recibos
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Gate>
            {allowed ? (
              receipts.isEmpty ? (
                <AwaitingData hint="Nenhum recibo emitido no período selecionado" />
              ) : (
                <div className="space-y-3">
                  <div className="flex items-center justify-between gap-3 rounded-lg bg-muted/40 p-3">
                    <div className="min-w-0">
                      <p className="text-sm text-muted-foreground">Emitidos</p>
                      <p className="text-[11px] text-muted-foreground/70">
                        {receipts.issuedCount}{' '}
                        {receipts.issuedCount === 1 ? 'recibo' : 'recibos'} no período
                      </p>
                    </div>
                    <p className="shrink-0 text-xl font-bold text-emerald-600">
                      {formatBRLShort(receipts.issuedTotal)}
                    </p>
                  </div>

                  {/* Cancelado NÃO some da tela nem entra no total: sumir
                      esconderia que houve cancelamento; somar diria que um
                      pagamento inexistente foi comprovado. */}
                  {receipts.cancelledCount > 0 && (
                    <div className="flex items-center justify-between gap-3 border-b py-2">
                      <p className="text-sm text-muted-foreground">Cancelados</p>
                      <div className="shrink-0 text-right">
                        <p className="text-sm font-semibold text-red-600">
                          {formatBRLShort(receipts.cancelledTotal)}
                        </p>
                        <p className="text-[11px] text-muted-foreground">
                          {receipts.cancelledCount}{' '}
                          {receipts.cancelledCount === 1 ? 'recibo' : 'recibos'} · fora do total
                        </p>
                      </div>
                    </div>
                  )}

                  {viewMode === 'detailed' && (
                    <div className="space-y-2 border-t pt-2">
                      {receiptRows.map((row) => (
                        <div
                          key={row.id}
                          className="flex items-center justify-between gap-3 border-b py-2 last:border-0"
                        >
                          <div className="min-w-0 flex-1">
                            <p className="truncate text-sm font-medium">{row.payerName}</p>
                            <p className="truncate text-[11px] text-muted-foreground">
                              {formatIsoDate(row.date)} · {row.stateLabel}
                            </p>
                          </div>
                          <p className="shrink-0 text-sm font-semibold">
                            {formatBRLShort(row.amount)}
                          </p>
                        </div>
                      ))}
                      <ListTail
                        total={receipts.rows.length}
                        shown={receiptRows.length}
                        noun="recibo"
                      />
                    </div>
                  )}
                </div>
              )
            ) : (
              <div className="space-y-2">
                {[
                  { s: 'Emitidos', v: 'R$ 7,8K' },
                  { s: 'Cancelados', v: 'R$ 0,4K' },
                ].map((row) => (
                  <div key={row.s} className="flex items-center justify-between border-b py-2 last:border-0">
                    <span className="text-sm">{row.s}</span>
                    <span className="text-sm">{row.v}</span>
                  </div>
                ))}
              </div>
            )}
          </Gate>
        </CardContent>
      </Card>

      {!allowed && (
        <Card className="border-amber-300/60 bg-amber-50 dark:border-amber-900/50 dark:bg-amber-900/10">
          <CardContent className="flex flex-col items-center py-6 text-center">
            <h3 className="text-sm font-semibold">Relatório de documentos</h3>
            <p className="mt-1 max-w-xs text-xs text-muted-foreground">
              Acompanhe ordens de serviço por status e com margem, contratos assinados e recibos
              emitidos no período. Disponível no plano PRO.
            </p>
            <Button3D color="amber" size="sm" className="mt-4" onClick={onUnlock}>
              <Crown className="mr-2 h-4 w-4" />
              Desbloquear com PRO
            </Button3D>
          </CardContent>
        </Card>
      )}
    </div>
  );
}

// ===== peças =====

/**
 * 'YYYY-MM-DD' → 'dd/MM'. Ancorado em meia-noite LOCAL (`new Date('...T00:00:00')`):
 * `new Date('2026-07-26')` parseia como UTC e em UTC-3 mostraria o dia anterior.
 */
function formatIsoDate(iso: string): string {
  if (!/^\d{4}-\d{2}-\d{2}/.test(iso)) return '';
  const d = new Date(`${iso.slice(0, 10)}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

interface KpiCardProps {
  icon: React.ReactNode;
  tint: string;
  label: string;
  value: string;
  valueClass: string;
  hint: string;
  allowed: boolean;
  onUnlock: () => void;
}

function KpiCard({ icon, tint, label, value, valueClass, hint, allowed, onUnlock }: KpiCardProps) {
  return (
    <Card>
      <CardContent className="p-3">
        <BlurOverlay allowed={allowed} onUnlock={onUnlock}>
          <div className="flex flex-col">
            <div className={`mb-1 flex h-6 w-6 items-center justify-center rounded-full ${tint}`}>
              {icon}
            </div>
            <p className="text-xs text-muted-foreground">{label}</p>
            <p className={`truncate text-base font-bold ${valueClass}`}>{value}</p>
            <p className="mt-0.5 truncate text-[11px] text-muted-foreground/70">{hint}</p>
          </div>
        </BlurOverlay>
      </CardContent>
    </Card>
  );
}
